import React, { useEffect } from 'react';
import styled, { css } from 'styled-components';
import { useHover } from 'react-laag';
import { token } from '@bynder/design-system';
import { ContentAlignment, HEIGHT_UNITS, SectionLayout } from '../../types';
import type { Column, Section } from '../../types';
import { useThemesetProperties } from '../../queries/themesets';
import ColumnPreview from './ColumnPreview';
import ColumnEdit from './ColumnEdit';
import { device } from '../../constants/styles';
import { InspectorSetting } from '../../constants/guides';
import { useActiveGuide, useActivePage } from '../../queries/guides';
import { StyledSection } from './Styles';
import { useGuideStore } from '../../stores/guideStore';
import { useInspectorStore } from '../../stores/inspectorStore';
import { CLASSNAMES } from '../guides/Inspector/constants';
import { useUploaderStore } from '../../stores/uploaderStore';
import { isHomeOrSinglePage } from '../../helpers/utils';
import { defaultWhite } from '../widgets/RichText/Config';
import { Color } from '../../types/color';
import { CroppedImage } from '@bynder/react-image-cropper-lib';
import { ActiveSplits, useReleaseToggle } from '../../helpers/releaseToggle';

interface Props {
  index: number;
  pageId: string;
  section: Section;
  sectionCount: number;
  isEditable: boolean;
  defaultBackgroundColor: Color | null;
}

const Section: React.FC<Props> = ({
  isEditable,
  section,
  index,
  pageId,
  defaultBackgroundColor,
}) => {
  const { data: activeGuide } = useActiveGuide();
  const { data: activePage } = useActivePage();

  if (activeGuide && activePage) {
    return isEditable ? (
      <EditModeSection
        isHeaderVisible={activePage.header.isVisible}
        section={section}
        index={index}
        pageId={activePage.id}
        themeBackgroundColor={defaultBackgroundColor}
      />
    ) : (
      <ViewModeSection
        section={section}
        pageId={pageId}
        themeBackgroundColor={defaultBackgroundColor}
      />
    );
  }
  return null;
};

export default Section;

const ViewModeSection: React.FC<{
  section: Section;
  pageId: string;
  themeBackgroundColor: Color | null;
}> = ({ themeBackgroundColor, section, pageId }) => {
  const { getThemesetColorById } = useThemesetProperties();

  const uploading = useUploaderStore.use.uploadingImages();
  const isUploadingInprogress = uploading.some(
    upload => upload.id === section.id,
  );

  const getBackgroundColor = () => {
    const backgroundColor = getThemesetColorById(section.backgroundColorId);
    const fallbackColor = defaultWhite;
    const themeColor = themeBackgroundColor;

    if (backgroundColor) {
      return backgroundColor;
    } else if (themeColor) {
      return themeColor;
    }
    return fallbackColor;
  };

  const { data: isCropperSplitActive } = useReleaseToggle(
    ActiveSplits.imageCropperRollout,
  );

  return (
    <StyledSection
      $isTypographyPickerOpen={false}
      id={section.id}
      $isUploadingInProgress={isUploadingInprogress}
      $height={section.appearance.height}
      className={CLASSNAMES.SECTION}
      $backgroundColor={getBackgroundColor().hexCode}
      $backgroundImage={!isCropperSplitActive && section?.asset?.imageUrl}
      $css={section?.style?.stylesheet}
    >
      <StyledSectionOverlayContainer className={CLASSNAMES.SECTION_OVERLAY} />

      {isCropperSplitActive && section?.asset?.imageUrl && (
        <StyledCroppedBackground>
          <CroppedImage src={section?.asset?.imageUrl} />
        </StyledCroppedBackground>
      )}
      <StyledSectionContainer
        $isTypographyPickerOpen={false}
        className={CLASSNAMES.SECTION_CONTAINER}
        $verticalPadding={section.appearance.paddingTop}
        $horizontalPadding={section.appearance.paddingLeft}
        $columnGap={section.appearance.columnSpacing}
        $width={section.appearance.width}
        $sectionLayout={section.sectionLayout}
        $contentAlignment={section.appearance.contentAlignment}
      >
        {section?.columns.map((column: Column, columnIndex: number) => (
          <ColumnPreview
            contentSpacing={section.appearance.contentSpacing}
            sectionLayout={section.sectionLayout}
            pageId={pageId}
            key={columnIndex}
            sectionId={section.id}
            columnIndex={columnIndex}
            widgets={column.widgets}
            numberOfColumns={section?.columns.length}
          />
        ))}
      </StyledSectionContainer>
    </StyledSection>
  );
};

const EditModeSection: React.FC<{
  section: Section;
  index: number;
  isHeaderVisible: boolean;
  pageId: string;
  themeBackgroundColor: Color | null;
}> = ({ section, index, isHeaderVisible, pageId, themeBackgroundColor }) => {
  const ref = React.useRef<HTMLLIElement>(null);
  const { getThemesetColorById } = useThemesetProperties();
  const { data: activeGuide } = useActiveGuide();
  const isSectionOnTop =
    isHomeOrSinglePage(activeGuide.type) || !isHeaderVisible;
  const setActiveSectionId = useGuideStore(s => s.setActiveSectionId);
  const activeSectionId = useGuideStore(s => s.activeSectionId);
  const setIsInspectorOpen = useInspectorStore(s => s.setIsInspectorOpen);
  const setActiveInspectorSetting = useInspectorStore(
    s => s.setActiveInspectorSetting,
  );
  const activeInspectorSetting = useInspectorStore(
    s => s.activeInspectorSetting,
  );

  const setActiveSectionComputedHeight = useInspectorStore(
    s => s.setActiveSectionComputedHeight,
  );

  const getBackgroundColor = () => {
    const fallBackColor = defaultWhite;
    const themeColor = themeBackgroundColor;
    const backgroundColor = getThemesetColorById(section.backgroundColorId);

    if (backgroundColor) {
      return backgroundColor;
    } else if (themeColor) {
      return themeColor;
    }
    return fallBackColor;
  };

  const [isHovering, hoverProps] = useHover({ hideOnScroll: false });

  const isTypographyPickerOpen = useGuideStore(s => s.isTypographyPickerOpen);

  const isActiveSection = section.id === activeSectionId;

  const isUploadInProgress = useUploaderStore.use
    .uploadingImages()
    .some(image => image.id === section.id);

  const handleClickSection = () => {
    if (ref.current) {
      setActiveSectionComputedHeight(
        Number(
          getComputedStyle(ref.current).height.replace(HEIGHT_UNITS.PX, ''),
        ),
      );
    }
    setActiveSectionId(section.id);
    setIsInspectorOpen(true);
    setActiveInspectorSetting(InspectorSetting.SECTION_VIEW);
  };
  useEffect(() => {
    if (isActiveSection && ref.current) {
      ref.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [isActiveSection]);

  const { data: isCropperSplitActive } = useReleaseToggle(
    ActiveSplits.imageCropperRollout,
  );

  return (
    <StyledSection
      ref={ref}
      id={section.id}
      $isTypographyPickerOpen={isTypographyPickerOpen}
      $height={section.appearance.height}
      $isActiveSection={isActiveSection}
      $isFirst={index === 0 && isSectionOnTop}
      className={CLASSNAMES.SECTION}
      data-testid="sectionArea"
      onClick={handleClickSection}
      $isUploadingInProgress={isUploadInProgress}
      $backgroundColor={getBackgroundColor().hexCode}
      $backgroundImage={!isCropperSplitActive && section?.asset?.imageUrl}
      $css={section?.style?.stylesheet}
      {...hoverProps}
    >
      <StyledSectionOverlayContainer className={CLASSNAMES.SECTION_OVERLAY} />
      {isCropperSplitActive && section?.asset?.imageUrl && (
        <StyledCroppedBackground>
          <CroppedImage src={section?.asset?.imageUrl} />
        </StyledCroppedBackground>
      )}
      <StyledEditModeSectionContainer
        $isTypographyPickerOpen={isTypographyPickerOpen}
        className={CLASSNAMES.SECTION_CONTAINER}
        $columnGap={section.appearance.columnSpacing}
        $verticalPadding={section.appearance.paddingTop}
        $horizontalPadding={section.appearance.paddingLeft}
        $contentAlignment={section.appearance.contentAlignment}
        $width={section.appearance.width}
        $sectionLayout={section.sectionLayout}
        $isActiveSection={isActiveSection}
        $isSectionPanelOpen={
          activeInspectorSetting === InspectorSetting.SECTION_VIEW
        }
        $isHovering={isHovering}
      >
        {section?.columns.map((column: Column, columnIndex: number) => (
          <ColumnEdit
            contentSpacing={section.appearance.contentSpacing}
            isHovering={isHovering}
            sectionLayout={section.sectionLayout}
            pageId={pageId}
            key={columnIndex}
            columnIndex={columnIndex}
            widgets={column.widgets}
            sectionId={section.id}
            numberOfColumns={section?.columns.length}
          />
        ))}
      </StyledEditModeSectionContainer>
    </StyledSection>
  );
};

interface SectionContainerProps {
  $width: number;
  $verticalPadding: number;
  $horizontalPadding: number;
  $sectionLayout: SectionLayout;
  $contentAlignment: ContentAlignment;
  $columnGap: number;
  $isActiveSection?: boolean;
  $isSectionPanelOpen?: boolean;
  $isHovering?: boolean;
  $isTypographyPickerOpen: boolean;
}

const StyledSectionContainer = styled.div<SectionContainerProps>`
  display: flex;
  width: ${props => props.$width}px;
  padding: ${props =>
    `${props.$verticalPadding}px ${props.$horizontalPadding}px`};
  min-height: auto;
  max-width: 100%;
  flex: 0 1 auto;
  ${props => !props.$isTypographyPickerOpen && `z-index: 1`};

  ${props => setContentAlignment(props.$contentAlignment)}

  @media ${device.mobile} {
    gap: ${token.spacing3};
  }
  @media ${device.tablet} {
    gap: ${props => props.$columnGap}px;
  }

  ${({ $sectionLayout }) => {
    switch ($sectionLayout) {
      case SectionLayout.DOUBLE_66_33:
        return css`
          > ul:first-child {
            width: 66.66%;
          }
          > ul:last-child {
            width: 33.33%;
          }
        `;
      case SectionLayout.DOUBLE_33_66:
        return css`
          > ul:first-child {
            width: 33.33%;
          }
          > ul:last-child {
            width: 66.66%;
          }
        `;
      case SectionLayout.SINGLE:
      case SectionLayout.DOUBLE_50_50:
      case SectionLayout.TRIPLE_33_33_33:
      default:
        return css`
          > ul {
            width: 100%;
          }
        `;
    }
  }}
`;

const StyledEditModeSectionContainer = styled(StyledSectionContainer)`
  ${({ $isActiveSection, $isSectionPanelOpen }) =>
    $isActiveSection &&
    $isSectionPanelOpen &&
    css`
      outline: 3px ${token.brandPrimary500} solid;
    `}

  ${({ $isActiveSection, $isHovering }) =>
    !$isActiveSection &&
    $isHovering &&
    css`
      outline: 3px ${token.brandPrimary100} solid;
      z-index: 1;
    `}
`;

const setContentAlignment = (alignment: ContentAlignment) => {
  switch (alignment) {
    case ContentAlignment.TOP_LEFT:
      return css`
        justify-content: flex-start;
        align-items: flex-start;
      `;
    case ContentAlignment.TOP:
    default:
      return css`
        justify-content: center;
        align-items: flex-start;
      `;
    case ContentAlignment.TOP_RIGHT:
      return css`
        justify-content: flex-end;
        align-items: flex-start;
      `;
    case ContentAlignment.LEFT:
      return css`
        justify-content: flex-start;
        align-items: center;
      `;
    case ContentAlignment.CENTER:
      return css`
        justify-content: center;
        align-items: center;
      `;
    case ContentAlignment.RIGHT:
      return css`
        justify-content: flex-end;
        align-items: center;
      `;
    case ContentAlignment.BOTTOM_LEFT:
      return css`
        justify-content: flex-start;
        align-items: flex-end;
      `;
    case ContentAlignment.BOTTOM:
      return css`
        justify-content: center;
        align-items: flex-end;
      `;
    case ContentAlignment.BOTTOM_RIGHT:
      return css`
        justify-content: flex-end;
        align-items: flex-end;
      `;
  }
};
const StyledSectionOverlayContainer = styled.div`
  position: absolute;
  height: 100%;
  width: 100%;
  z-index: 1;
`;

const StyledCroppedBackground = styled.div`
  position: absolute;
  overflow: hidden;
  width: 100%;
  height: 100%;
`;
